import React from 'react';
import { Card, CardContent, Badge } from '@components/Card';
import { RevenueCaps, RevenueCapCompany } from '@/types/dashboard';
import { formatCnpj } from '@/types/company';

interface RevenueCapPanelProps {
  data: RevenueCaps;
}

const formatBRL = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value: number): string =>
  `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** Formata 'YYYY-MM' como "agosto de 2026". */
const formatMonthLabel = (month: string): string => {
  const [year, monthNum] = month.split('-').map(Number);
  if (!year || !monthNum) return month;
  return new Date(year, monthNum - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
};

/** Recebido no mês (base do teto e do balanceamento); fallback para APIs antigas. */
const receivedOf = (company: RevenueCapCompany): number =>
  company.received_revenue_month ??
  (company.captured_revenue_month ?? 0) + (company.direct_payment_revenue_month ?? 0);

/**
 * Painel de recebido do mês por empresa, com o teto e a regra do roteamento de
 * pagamentos. O recebido (cartão capturado + PIX QR Code + transferência do
 * CRM) é o mesmo número que o roteamento usa no teto e no balanceamento.
 */
export const RevenueCapPanel: React.FC<RevenueCapPanelProps> = ({ data }) => {
  const weighted = data.routing_mode === 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-600 capitalize">{formatMonthLabel(data.month)}</p>
        {!data.routing_enabled ? (
          <span className="text-xs text-slate-400">Roteamento desligado</span>
        ) : (
          <span className="text-xs text-slate-500">
            {weighted ? 'Balanceamento por peso' : 'Prioridade por teto'}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.companies.map(company => {
          const received = receivedOf(company);
          const paid = company.paid;
          const percent = company.cap_consumed_percent ??
            (company.monthly_cap != null && company.monthly_cap > 0
              ? (received / company.monthly_cap) * 100
              : null);
          const barColor = percent != null && percent >= 95
            ? 'bg-red-500'
            : percent != null && percent >= 70
              ? 'bg-amber-500'
              : 'bg-green-500';

          return (
            <Card key={company.company_id}>
              <CardContent>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">{company.company_name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{formatCnpj(company.company_document)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {weighted ? (
                      <>
                        {company.weight != null && company.enabled && (
                          <Badge variant="info">Peso {company.weight}</Badge>
                        )}
                        {!company.enabled && <Badge variant="neutral">Inativa no roteamento</Badge>}
                        {company.cap_reached && <Badge variant="danger">Teto atingido</Badge>}
                      </>
                    ) : (
                      <>
                        {company.is_default && <Badge variant="success">Padrão</Badge>}
                        {company.routing_suspended && <Badge variant="danger">Roteado p/ empresa padrão</Badge>}
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-small text-slate-500">Recebido no mês</p>
                  <p className="text-2xl font-bold text-slate-900">{formatBRL(received)}</p>
                  {paid && received > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Cartão: {formatBRL(paid.card)} · PIX: {formatBRL(paid.pix_qrcode)}
                      {paid.manual_transfer > 0 && (
                        <>
                          {' '}·{' '}
                          <span title="Transferência/depósito confirmado na proposta do CRM (Processar pagamento)">
                            Transferência: {formatBRL(paid.manual_transfer)}
                          </span>
                        </>
                      )}
                    </p>
                  )}
                  <p className="text-sm text-slate-500 mt-1">
                    Faturamento NF-e: {formatBRL(company.invoice_revenue_month)}
                  </p>
                  {weighted && company.target_share_percent != null && (
                    <p className="text-sm text-slate-600 mt-1">
                      Participação: {formatPercent(company.real_share_percent ?? 0)}{' '}
                      <span className="text-slate-400">(alvo {formatPercent(company.target_share_percent)})</span>
                    </p>
                  )}
                </div>

                {company.monthly_cap != null && (
                  <div className="mt-4">
                    <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className={`h-2 rounded-full ${barColor}`}
                        style={{ width: `${Math.min(100, Math.max(0, percent ?? 0))}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-600">
                        {company.cap_reached
                          ? `Teto de ${formatBRL(company.monthly_cap)} atingido`
                          : `Faltam ${formatBRL(company.cap_remaining ?? 0)} para o teto de ${formatBRL(company.monthly_cap)}`}
                      </p>
                      {percent != null && (
                        <span className="text-xs font-medium text-slate-600">{formatPercent(percent)}</span>
                      )}
                    </div>
                  </div>
                )}
                {weighted && company.monthly_cap == null && company.enabled && (
                  <p className="text-xs text-slate-500 mt-4">Sem teto: recebe o excedente das empresas com teto.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
